import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ResetLinkDialog } from '../components/ResetLinkDialog';

const ROLES = ['admin', 'technician', 'operator', 'viewer'];
const STATUSES = ['pending', 'active', 'deactivated'];

const STATUS_CLS = {
  pending: 'bg-amber-100 text-amber-800',
  active: 'bg-green-100 text-green-800',
  deactivated: 'bg-slate-200 text-slate-700',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const Users = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [resetting, setResetting] = useState(null); // user being password-reset
  const [editingEmail, setEditingEmail] = useState(null);
  const [linkFor, setLinkFor] = useState(null);

  const forceLogout = async (u) => {
    if (!window.confirm(`Log ${u.name} out on every device? They will need to sign in again.`)) return;
    try {
      const res = await axios.post(`${API}/users/${u.id}/force-logout`);
      toast.success(res.data?.message || 'Logged out everywhere');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to end sessions');
    }
  };

  if (user?.role !== 'admin') {
    navigate('/');
  }

  const fetchAll = async () => {
    try {
      const [u, a] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/audit-log?limit=100`),
      ]);
      setUsers(u.data || []);
      setAudit(a.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const updateUser = async (u, body, successMessage = 'Updated') => {
    try {
      await axios.put(`${API}/users/${u.id}`, body);
      toast.success(successMessage);
      fetchAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
      return false;
    }
  };

  const filteredUsers = users.filter((u) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return u.status === 'pending';
    if (filter === 'active') return u.status === 'active';
    if (filter === 'deactivated') return u.status === 'deactivated';
    return true;
  });

  const pendingCount = users.filter((u) => u.status === 'pending').length;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center flex-wrap gap-3">
          <div>
            <Button variant="outline" onClick={() => navigate('/')} className="mb-2" data-testid="back-to-dashboard-btn">
              ← Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-sm text-slate-600 mt-1">
              {users.length} total users
              {pendingCount > 0 && (
                <span className="ml-3 inline-block bg-amber-100 text-amber-800 rounded px-2 py-0.5 font-medium">
                  {pendingCount} pending approval
                </span>
              )}
            </p>
          </div>
          <Button variant="outline" onClick={onLogout}>Logout</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-2">
          <span className="text-sm text-slate-600 mr-2 self-center">Filter:</span>
          {[
            { v: 'all', label: 'All' },
            { v: 'pending', label: `Pending (${pendingCount})` },
            { v: 'active', label: 'Active' },
            { v: 'deactivated', label: 'Deactivated' },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilter(f.v)}
              data-testid={`user-filter-${f.v}`}
              className={`text-xs rounded-full px-3 py-1 border ${
                filter === f.v ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Last login</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-6 text-center text-slate-500">No users match this filter.</td></tr>
              )}
              {filteredUsers.map((u) => {
                const isSelf = u.id === user.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{u.email}{isSelf && <span className="ml-2 text-xs text-slate-500">(you)</span>}</td>
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => updateUser(u, { role: e.target.value })}
                        className="border rounded px-2 py-1 text-sm bg-white"
                        data-testid={`role-select-${u.id}`}
                        disabled={isSelf}
                        title={isSelf ? "You can't change your own role" : ''}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs rounded px-2 py-0.5 font-medium ${STATUS_CLS[u.status] || ''}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(u.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {u.status === 'pending' && (
                          <Button size="sm" onClick={() => updateUser(u, { status: 'active' })} data-testid={`approve-${u.id}`}>
                            Approve
                          </Button>
                        )}
                        {u.status === 'active' && !isSelf && (
                          <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => updateUser(u, { status: 'deactivated' })} data-testid={`deactivate-${u.id}`}>
                            Deactivate
                          </Button>
                        )}
                        {u.status === 'deactivated' && (
                          <Button size="sm" variant="outline" onClick={() => updateUser(u, { status: 'active' })} data-testid={`reactivate-${u.id}`}>
                            Reactivate
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setResetting(u)} data-testid={`reset-pw-${u.id}`}>
                          Reset password
                        </Button>
                        {u.status === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => setLinkFor(u)} data-testid={`reset-link-${u.id}`}>
                            Create reset link
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setEditingEmail(u)} data-testid={`edit-email-${u.id}`}>
                          Edit email
                        </Button>
                        {!isSelf && (
                          <Button size="sm" variant="outline" onClick={() => forceLogout(u)} data-testid={`force-logout-${u.id}`}>
                            Log out everywhere
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b">
            <h2 className="text-base font-semibold text-slate-900">Audit Log</h2>
            <p className="text-xs text-slate-600">Recent 100 user-management actions</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Actor</th>
                  <th className="px-4 py-2">Action</th>
                  <th className="px-4 py-2">Target</th>
                  <th className="px-4 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-6 text-center text-slate-500">No audit entries yet.</td></tr>
                )}
                {audit.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-4 py-2 text-slate-600 text-xs whitespace-nowrap">{formatDate(row.timestamp)}</td>
                    <td className="px-4 py-2 text-slate-700">{row.actor_name || row.actor_email || row.actor_id}</td>
                    <td className="px-4 py-2 font-medium">{row.action}</td>
                    <td className="px-4 py-2 text-slate-700">{row.target_user_email || '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {row.details && Object.keys(row.details).length > 0 ? (
                        <pre className="whitespace-pre-wrap font-mono text-xs">{JSON.stringify(row.details)}</pre>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {linkFor && <ResetLinkDialog target={linkFor} onClose={() => { setLinkFor(null); fetchAll(); }} />}
      {editingEmail && (
        <EditEmailDialog
          target={editingEmail}
          isSelf={editingEmail.id === user.id}
          onClose={() => setEditingEmail(null)}
          onSave={async (email) => {
            const ok = await updateUser(editingEmail, { email },
              `Email updated · ${editingEmail.name} was logged out on every device`);
            if (ok) setEditingEmail(null);
          }}
        />
      )}
      {resetting && (
        <ResetPasswordDialog
          target={resetting}
          onClose={() => setResetting(null)}
          onSuccess={() => { setResetting(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

const EditEmailDialog = ({ target, isSelf, onClose, onSave }) => {
  const [email, setEmail] = useState(target.email);
  const [saving, setSaving] = useState(false);
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="edit-email-dialog">
        <DialogHeader>
          <DialogTitle>Edit email</DialogTitle>
          <DialogDescription>
            {target.name} will be logged out on every device and must sign in with the new email.
            {isSelf && ' This includes you.'}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={async (e) => {
          e.preventDefault(); setSaving(true); await onSave(email.trim()); setSaving(false);
        }}>
          <div className="space-y-1">
            <Label htmlFor="edit-email">Email</Label>
            <Input id="edit-email" type="email" required data-testid="edit-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || email.trim() === target.email} data-testid="save-email-btn">
              {saving ? 'Saving…' : 'Save email'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ResetPasswordDialog = ({ target, onClose, onSuccess }) => {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      await axios.post(`${API}/users/${target.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${target.email}. Share it with them securely.`);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>{target.email} — {target.name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="new-pw">New password</Label>
            <Input id="new-pw" data-testid="new-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} />
            <p className="text-xs text-slate-500">Min 8 characters. Avoid common passwords ("password", "12345678", etc.) — the server will reject them.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pw2">Confirm password</Label>
            <Input id="new-pw2" data-testid="confirm-password-input" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="confirm-reset-btn">
              {submitting ? 'Saving…' : 'Reset password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Users;
